// Source string preprocessor that adapts agent / cell-template markdown
// to the standard remark-directive grammar. The platform's content uses
// single-line `:::name[label]{attrs}:::` directives — a non-standard
// shorthand that mdast-util-directive does NOT recognize. This function
// rewrites them to single-colon leaf-directive form (`:name[label]{attrs}`)
// which mdast-util-directive parses cleanly.
//
// Apply BEFORE feeding markdown to from-markdown / remark-parse. After
// this pass, the parser produces real `leafDirective` nodes for widget /
// stat / card directives.
//
// History: ported from apps/web/src/components/canvas/markdown-renderer.tsx
// (lines ~233–252) so that both the chat render path and the editor
// (@rmint/editor) share one implementation. Behavior must remain
// byte-for-byte identical with the original — chat-stream tests assert
// the rewrite, and any drift is a regression.
export function normalizeAgentMarkdown(md: string): string {
  // 1a. Single-line widget directive whose attrs may themselves contain `}`
  //     (e.g. founder-decision's options='[{"label":"Yes"...}]'). The naive
  //     `[^}]*` rule below stops at the FIRST `}` — it would rewrite the
  //     leading `:::`→`:` based on an inner brace and leave the real closing
  //     `}:::` intact, leaking a stray `:::` after the rendered widget. Match
  //     the whole single line up to the trailing `}:::` first. `[^\n]*` keeps
  //     this to one line so multi-line containers (rule 1b + locked test #6)
  //     are untouched; greedy + the required `:{2,3}` anchor backtracks to the
  //     LAST `}:::` on the line, so internal `}` in quoted attrs are kept.
  let result = md.replace(/:{2,3}widget\[([^\]]*)\]\{([^\n]*)\}:{2,3}/g, ':widget[$1]{$2}');

  // 1b. Remaining widget directives: the single-line no-brace form and the
  //     multi-line container OPEN line (`:::widget[x]{attrs}\n…:::`), whose
  //     closing ::: lives on a later line (`:{0,3}` tolerates 0 trailing
  //     colons). Runs after 1a, so 1a's `:`-prefixed output is never re-hit.
  result = result.replace(/:{2,3}widget\[([^\]]*)\]\{([^}]*)\}:{0,3}/g, ':widget[$1]{$2}');

  // 2. Inline leaf directives (stat, card): demote to single-colon when
  //    the LLM squashed onto one line — common for quick nav-link cards.
  result = result.replace(/:{2,3}(stat|card)\{([^}]*)\}:{0,3}/g, ':$1{$2}');
  result = result.replace(/:{2,3}(stat|card)\[([^\]]*)\]\{([^}]*)\}:{0,3}/g, ':$1[$2]{$3}');

  // 3. Slot directives (with attrs and without attrs forms):
  //    :::slot[name]{attrs}:::  → :slot[name]{attrs}
  //    :::slot[name]:::          → :slot[name]
  //    Mirrors the widget pattern. Required for SlotInspector (Task 16)
  //    and the slot-grammar round-trip property test (Task 3).
  result = result.replace(/:{2,3}slot\[([^\]]*)\]\{([^}]*)\}:{0,3}/g, ':slot[$1]{$2}');
  result = result.replace(/:{2,3}slot\[([^\]]*)\]:{0,3}/g, ':slot[$1]');

  // 4. URL-encode spaces in prompt links
  result = result.replace(
    /\[([^\]]+)\]\(\/prompt\/([^)]+)\)/g,
    (_match, label, promptText) => {
      const encoded = encodeURIComponent(promptText.trim());
      return `[${label}](/prompt/${encoded})`;
    },
  );

  return result;
}
